// src/components/Navbar.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, X, ArrowRight, Music, Mic2, MessageCircle, Instagram, Phone } from 'lucide-react';
import logoImage from '../assets/images/apemlogo.png';
import getUser from '../constants/getUser';
import { fetchSearchResults } from '../constants/api/youtube';

const extractArtistName = (video) => {
    const title = video?.title || '';
    const titleParts = title.split(/\s[-–—]\s/);
    const channelTitle = video?.channelTitle || '';
    const ignoredChannels = ['youtube', 'historico apem'];
    const fallbackChannel = ignoredChannels.includes(channelTitle.toLowerCase()) ? '' : channelTitle;
    const artist = (titleParts.length > 1 ? titleParts[0] : fallbackChannel)
        .replace(/\s*\[[^\]]*]\s*/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    return artist.length >= 2 ? artist : '';
};

const buildArtistSuggestions = (videos) => {
    const seen = new Set();

    return videos.reduce((artists, video) => {
        const name = extractArtistName(video);
        const key = name.toLowerCase();

        if (!name || seen.has(key)) {
            return artists;
        }

        seen.add(key);
        return [...artists, { name, video }];
    }, []).slice(0, 5);
};

const Navbar = ({ title = "A Próxima é Minha", showLogo = true }) => {
    // ... (Component logic remains the same as the previous version) ...
    const navigate = useNavigate();
    const location = useLocation();
    const [showContacts, setShowContacts] = useState(false);
    const showContactButton = location.pathname === '/home';
    const isResultPage = location.pathname === '/resultado';
    const initialSearchQuery = new URLSearchParams(location.search).get("query") || "";
    const [query, setQuery] = useState(initialSearchQuery);
    const [isTyping, setIsTyping] = useState(!!initialSearchQuery);
    const [hasTypedSearch, setHasTypedSearch] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [loadingSuggestions, setLoadingSuggestions] = useState(false);

    useEffect(() => {
        const currentQueryParam = new URLSearchParams(location.search).get("query") || "";
        setQuery(currentQueryParam);
        setIsTyping(isResultPage ? false : !!currentQueryParam);
        setHasTypedSearch(false);
        setSuggestions([]);
        setLoadingSuggestions(false);
    }, [location.search, isResultPage]);

    useEffect(() => {
        const trimmedQuery = query.trim();
        let isActive = true;

        if (!hasTypedSearch || !isTyping || trimmedQuery.length < 3) {
            setSuggestions([]);
            setLoadingSuggestions(false);
            return;
        }

        setLoadingSuggestions(true);
        const timer = setTimeout(async () => {
            try {
                let clientId = 0;

                try {
                    const user = await getUser();
                    clientId = user?.client_id || 0;
                } catch (error) {
                    clientId = 0;
                }

                const data = await fetchSearchResults(trimmedQuery, clientId, 'suggest');

                if (isActive) {
                    setSuggestions((data || []).slice(0, 8));
                }
            } catch (error) {
                if (isActive) setSuggestions([]);
            } finally {
                if (isActive) setLoadingSuggestions(false);
            }
        }, 350);

        return () => {
            isActive = false;
            clearTimeout(timer);
        };
    }, [query, isTyping, hasTypedSearch]);

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        const trimmedQuery = query.trim();
        if (trimmedQuery.length > 0) {
            navigate(`/resultado?query=${trimmedQuery}`);
            setSuggestions([]);
            setHasTypedSearch(false);
        } else {
            setIsTyping(false);
        }
    };

    const handleSuggestionClick = (video) => {
        const title = video.title || query;
        setQuery(title);
        setSuggestions([]);
        setHasTypedSearch(false);
        navigate(`/resultado?query=${encodeURIComponent(title)}`, {
            state: { selectedVideo: video },
        });
    };

    const handleSearchChange = (e) => {
        setQuery(e.target.value);
        setHasTypedSearch(true);
    };

    const clearSearch = () => {
        setQuery('');
        setIsTyping(false);
        setHasTypedSearch(false);
        setSuggestions([]);
        // navigate(location.pathname); // Optional: Clear URL query param
    };

    const handleArtistClick = (artistName) => {
        setQuery(artistName);
        setSuggestions([]);
        setHasTypedSearch(false);
        navigate(`/resultado?query=${encodeURIComponent(artistName)}`);
    };

    const artistSuggestions = buildArtistSuggestions(suggestions);

    return (
        <>
        <div style={styles.navbar}>
            <div style={styles.leftPlaceholder}>
                {showContactButton && <button onClick={() => setShowContacts(true)} style={styles.actionButton} aria-label="Abrir contatos" title="Contatos">
                    <MessageCircle size={23} color={styles.iconColor} />
                </button>}
            </div>

            <div style={styles.middleContent}>
                {!isTyping ? (
                    <>
                        {showLogo && <img src={logoImage} alt="Logo" style={styles.logo} />}
                        <span style={styles.title}>{title}</span>
                    </>
                ) : (
                    <form onSubmit={handleSearchSubmit} style={styles.searchForm}>
                        <input
                            type="text"
                            placeholder="Buscar música..."
                            value={query}
                            onChange={handleSearchChange}
                            onInput={handleSearchChange}
                            autoFocus
                            style={styles.searchInput}
                        />
                        {hasTypedSearch && isTyping && query.trim().length >= 3 && (
                            <div
                                style={styles.suggestionsPanel}
                                onWheel={(event) => event.stopPropagation()}
                                onTouchMove={(event) => event.stopPropagation()}
                            >
                                {loadingSuggestions && suggestions.length === 0 ? (
                                    <div style={styles.suggestionEmpty}>Buscando musicas...</div>
                                ) : suggestions.length > 0 ? (
                                    <>
                                        {artistSuggestions.length > 0 && (
                                            <>
                                                <div style={styles.suggestionSectionTitle}>Bandas e artistas</div>
                                                {artistSuggestions.map((artist) => (
                                                    <button
                                                        key={artist.name}
                                                        type="button"
                                                        style={styles.artistItem}
                                                        onClick={() => handleArtistClick(artist.name)}
                                                    >
                                                        <span style={styles.artistIcon}>
                                                            <Mic2 size={16} color="#f0f0f0" />
                                                        </span>
                                                        <span style={styles.artistName}>{artist.name}</span>
                                                        <Search size={15} color="#a0a0ff" />
                                                    </button>
                                                ))}
                                            </>
                                        )}
                                        <div style={styles.suggestionSectionTitle}>Musicas</div>
                                        {suggestions.map((video) => {
                                            const thumbnail = video.thumbnail?.[0]?.url || `https://i.ytimg.com/vi/${video.videoId}/mqdefault.jpg`;

                                            return (
                                                <button
                                                    key={video.videoId}
                                                    type="button"
                                                    style={styles.suggestionItem}
                                                    onClick={() => handleSuggestionClick(video)}
                                                >
                                                    <img src={thumbnail} alt="" style={styles.suggestionThumb} />
                                                    <div style={styles.suggestionTextWrap}>
                                                        <span style={styles.suggestionTitle}>{video.title || 'Musica'}</span>
                                                        <span style={styles.suggestionMeta}>{video.source === 'history' ? 'Historico APEM' : 'YouTube'}</span>
                                                    </div>
                                                    <Music size={16} color="#a0a0ff" />
                                                </button>
                                            );
                                        })}
                                    </>
                                ) : (
                                    <div style={styles.suggestionEmpty}>Nenhuma musica encontrada</div>
                                )}
                            </div>
                        )}
                    </form>
                )}
            </div>

            <div style={styles.rightActions}>
                {isTyping ? (
                    <>
                        <button onClick={clearSearch} style={styles.actionButton} aria-label="Limpar busca">
                            <X size={24} color={styles.iconColor} />
                        </button>
                        <button type="submit" onClick={handleSearchSubmit} style={styles.actionButton} aria-label="Buscar">
                            <ArrowRight size={24} color={styles.iconColor} />
                        </button>
                    </>
                ) : (
                    <button onClick={() => setIsTyping(true)} style={styles.actionButton} aria-label="Iniciar busca">
                        <Search size={24} color={styles.iconColor} />
                    </button>
                )}
            </div>
        </div>
        {showContactButton && showContacts && <div style={styles.contactOverlay} onClick={() => setShowContacts(false)}>
            <section style={styles.contactDialog} role="dialog" aria-modal="true" aria-labelledby="navbar-contact-title" onClick={(event) => event.stopPropagation()}>
                <h2 id="navbar-contact-title" style={{ margin: '0 0 13px', fontSize: '18px' }}>Fale com a gente</h2>
                <a href="https://www.instagram.com/aproximaeminha/" target="_blank" rel="noopener noreferrer" style={styles.contactLink}><Instagram size={19} />Instagram</a>
                <a href="https://wa.me/5534993358669" target="_blank" rel="noopener noreferrer" style={styles.contactLink}><Phone size={19} />WhatsApp</a>
                <button onClick={() => setShowContacts(false)} style={styles.contactClose}>Fechar</button>
            </section>
        </div>}
        </>
    );
};

// --- Refined Styles Object ---
const styles = {
    navbar: {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%', // Explicitly 100% width
        height: '60px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: '#1a1a2e',
        padding: '0 10px', // Reduced horizontal padding slightly
        zIndex: 1050,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.5)',
        color: '#f0f0f0',
        boxSizing: 'border-box', // CRITICAL: Include padding in the 100% width calculation
        overflow: 'visible',
    },
    leftPlaceholder: {
        width: '44px', // Match the potential width of right actions (2 icons + gap)
        flexShrink: 0,
        boxSizing: 'border-box', // Apply everywhere for consistency
    },
    middleContent: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexGrow: 1,
        overflow: 'visible',
        minWidth: '0', // ESSENTIAL: Allows this flex item to shrink below its content size
        padding: '0 5px', // Reduced padding inside middle section
        boxSizing: 'border-box',
    },
    rightActions: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px', // Reduced gap between buttons
        width: 'auto',
        flexShrink: 0,
        justifyContent: 'flex-end',
        minWidth: '44px', // Ensure minimum space, matching placeholder roughly
        boxSizing: 'border-box',
    },
    logo: {
        width: '30px',
        height: '30px',
        marginRight: '8px',
        objectFit: 'contain',
        flexShrink: 0, // Prevent logo from shrinking disproportionately
    },
    title: {
        color: '#f0f0f0',
        fontSize: '18px',
        fontWeight: 'bold',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        minWidth: '0', // Allow title itself to shrink if needed (though ellipsis should handle it)
    },
    searchForm: {
        width: '100%',
        display: 'flex',
        position: 'relative',
        minWidth: '0', // Ensure form can shrink
    },
    contactOverlay: {
        position: 'fixed',
        inset: 0,
        zIndex: 1200,
        display: 'grid',
        placeItems: 'center',
        padding: '20px',
        background: 'rgba(5, 6, 15, .74)',
        backdropFilter: 'blur(5px)',
    },
    contactDialog: {
        width: 'min(360px, 100%)',
        padding: '20px',
        border: '1px solid #3a3c52',
        borderRadius: '7px',
        color: '#f3f4fa',
        background: '#202136',
    },
    contactLink: {
        width: '100%',
        minHeight: '43px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '9px',
        marginTop: '8px',
        border: '1px solid #3b3d54',
        borderRadius: '5px',
        color: '#e9eaf3',
        background: '#292b42',
        textDecoration: 'none',
        fontSize: '13px',
        boxSizing: 'border-box',
    },
    contactClose: {
        width: '100%',
        minHeight: '43px',
        marginTop: '8px',
        border: '1px solid #3b3d54',
        borderRadius: '5px',
        color: '#b9f0da',
        background: '#292b42',
        font: 'inherit',
        fontSize: '13px',
        cursor: 'pointer',
    },
    searchInput: {
        width: '100%',
        fontSize: '16px',
        backgroundColor: '#2a2a4e',
        border: '1px solid #4a4a6e',
        borderRadius: '6px',
        padding: '6px 10px',
        color: '#f0f0f0',
        outline: 'none',
        boxSizing: 'border-box', // Already had this, which is good
        minWidth: '0', // Allow input to shrink if container forces it
    },
    suggestionsPanel: {
        position: 'absolute',
        top: '44px',
        left: '-44px',
        right: '-44px',
        maxHeight: '55vh',
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        WebkitOverflowScrolling: 'touch',
        touchAction: 'pan-y',
        backgroundColor: '#141426',
        border: '1px solid #34345c',
        borderRadius: '10px',
        boxShadow: '0 14px 30px rgba(0,0,0,0.45)',
        padding: '6px',
        zIndex: 1200,
    },
    suggestionSectionTitle: {
        padding: '8px 8px 5px',
        color: '#a0a0b0',
        fontSize: '11px',
        fontWeight: 800,
        textTransform: 'uppercase',
        letterSpacing: 0,
    },
    artistItem: {
        width: '100%',
        minHeight: '44px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '8px',
        border: 'none',
        borderRadius: '8px',
        backgroundColor: '#20203a',
        color: '#f0f0f0',
        textAlign: 'left',
        cursor: 'pointer',
        marginBottom: '4px',
    },
    artistIcon: {
        width: '30px',
        height: '30px',
        borderRadius: '50%',
        backgroundColor: '#4a4a6e',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    artistName: {
        flex: 1,
        minWidth: 0,
        color: '#ffffff',
        fontSize: '14px',
        fontWeight: 800,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    suggestionItem: {
        width: '100%',
        minHeight: '64px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '8px',
        border: 'none',
        borderRadius: '8px',
        backgroundColor: 'transparent',
        color: '#f0f0f0',
        textAlign: 'left',
        cursor: 'pointer',
    },
    suggestionThumb: {
        width: '64px',
        height: '42px',
        borderRadius: '6px',
        objectFit: 'cover',
        backgroundColor: '#2a2a4e',
        flexShrink: 0,
    },
    suggestionTextWrap: {
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: '3px',
    },
    suggestionTitle: {
        fontSize: '13px',
        fontWeight: 700,
        color: '#ffffff',
        lineHeight: 1.25,
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
    },
    suggestionMeta: {
        fontSize: '11px',
        color: '#a0a0b0',
    },
    suggestionEmpty: {
        padding: '14px 12px',
        color: '#a0a0b0',
        fontSize: '13px',
        textAlign: 'center',
    },
    actionButton: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: '6px', // Slightly increased padding for tap area
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        transition: 'background-color 0.2s ease',
        flexShrink: 0, // Prevent icons from shrinking
    },
    iconColor: '#a0a0ff',
};


export default Navbar;
